import store from './store/store';
import env from './env';
const axios = require('axios');
import { objects } from './utils/objects';
import ApiError from './utils/ApiError';

const API_MIN_VERSION = '2021.04.001'; // year.month.increments

export default {
    url: localStorage.apiEndpoint || '',

    get(endpoint, data = null, opts = {}) {
        endpoint += this.queryString(data);
        return this.request(endpoint, 'GET', null, opts);
    },

    post(endpoint, data, opts = {}) {
        return this.request(endpoint, 'POST', data, opts);
    },

    put(endpoint, data, opts = {}) {
        return this.request(endpoint, 'PUT', data, opts);
    },

    delete(endpoint, data = null, opts = {}) {
        endpoint += this.queryString(data);
        return this.request(endpoint, 'DELETE', null, opts);
    },

    async download(endpoint, data = null, opts = {}) {
        let method = opts.method ? opts.method.toLowerCase() : 'get';
        delete opts.method;
        let filename = opts.filename;
        delete opts.filename;
        let open = !!opts.open;
        delete opts.open;
        opts.responseType = 'blob';
        let blob = await this[method](endpoint, data, opts);
        if (open) {
            objects.openBlob(blob);
        } else {
            objects.downloadBlob(blob, filename);
        }
    },

    request(endpoint, method, data = null, opts = { timeout: 0, responseType: 'json' }) {
        if (!this.url) this.url = env.API_ENDPOINT;

        // remove non-axios options
        let supressErrors = !!opts.supressErrors;
        delete opts.supressErrors;

        let options = {
            method,
            url: `${this.url}/${endpoint.replace(/^\//, '')}`,
            headers: { 'Cache-Control': 'no-cache' },
            ...opts
        };

        if (data) {
            let contentType = opts.contentType || 'application/json';

            if (data instanceof FormData) {
                contentType = null;
                options.data = data;
            } else if (contentType === 'multipart/form-data') {
                contentType = null;
                options.data = this.formData(data);
            } else if (data instanceof URLSearchParams) {
                contentType = null;
                options.data = data;
            } else if (contentType === 'application/x-www-form-urlencoded') {
                if (typeof data === 'object') {
                    contentType = null;
                    options.data = this.urlSearchParams(data);
                } else {
                    contentType = 'application/x-www-form-urlencoded;charset=UTF-8';
                    options.data = data;
                }
            } else if (contentType === 'application/json') {
                options.data = JSON.stringify(data);
            } else {
                options.data = data;
            }

            if (contentType) options.headers['Content-Type'] = contentType;
        }

        if (store.getters['auth/authToken'] && store.getters['base/isAuthRoute']) {
            options.headers['Authorization'] = `Bearer ${store.getters['auth/authToken']}`;
        }

        return axios.request(options)
            .then(async res => {
                if (res.headers['x-new-auth-token']) {
                    store.commit('auth/setAuthToken', res.headers['x-new-auth-token']);
                    setTimeout(() => store.dispatch('user/load'));
                }
                let apiVersion = res.headers['x-api-version'];
                if (apiVersion !== store.getters['base/apiVersion']) {
                    store.commit('base/apiVersion', apiVersion);
                    if (apiVersion < API_MIN_VERSION) {
                        store.commit(
                            'base/developerError',
                            `API version ${apiVersion} is below minimum required version of ${API_MIN_VERSION}`
                        );
                    }
                }
                return res.data;
            })
            .catch(async error => {
                if (supressErrors) return;

                if (error.name === 'AbortError') {
                    const err = new ApiError('GEN-RTO', 408, 'Request Timeout');
                    await store.dispatch('notification/set', { message: err.message, type: 'error' });
                    throw error;
                }

                if (error.response) {
                    // non 2xx response
                    if (error.response.status === 401) {
                        await store.dispatch('auth/logout');
                        return;
                    } else if (error.response.data) {
                        let errText = '', errDetails, data;
                        if (error.response.data instanceof Blob) {
                            data = await error.response.data.text();
                            if (error.response.data.type === 'application/json') {
                                data = JSON.parse(data);
                            }
                        } else {
                            data = error.response.data;
                        }
                        if (typeof data === 'object') {
                            if (data.error) {
                                errText = data.error;
                                errDetails = data.details;
                            } else {
                                errDetails = data;
                            }
                        } else {
                            errText = data;
                        }
                        const err = new ApiError(errText, error.response.status, error.response.statusText, errDetails);

                        // Special message for service plan limit errors
                        if (err.code.startsWith('LIM-')) {
                            const appName = store.getters['base/appName'];
                            const salesEmail = store.getters['base/salesEmail'];
                            const marketingSite = store.getters['base/marketingSite'];
                            let body = `
                                ${err.message}
                                <br><br>
                                If you would like to upgrade your service plan, please contact a ${appName} representative at <a href="mailto:${salesEmail}">${salesEmail}</a>.
                            `;
                            if (!store.getters['base/appBranding']) {
                                body += ` You can also view the available plans on the <a href="${marketingSite}" target="_blank">${appName} website</a>.`;
                            }
                            store.dispatch('dialog/set', {
                                type: 'info',
                                header: 'Service Plan Limit Reached',
                                dialogProps: { width: '450px' },
                                body,
                            });
                            throw err;
                        }

                        await store.dispatch('notification/set', { message: err.message, type: 'error' });
                        throw err;
                    }
                } else if (error.request) {
                    // no server response
                    await store.dispatch('notification/set', { message: `Network connection error`, type: 'error' });
                } else {
                    // pre-request error
                }

                throw error;
            });
    },

    queryString(data) {
        let params = this.urlSearchParams(data);
        return params ? '?' + params : '';
    },

    urlSearchParams(data) {
        let params = new URLSearchParams();
        if (data) {
            for (let key in data) {
                if (data[key] === undefined) continue;
                if (Array.isArray(data[key])) {
                    for (let val of data[key]) {
                        params.append(key, val);
                    }
                } else {
                    params.append(key, data[key]);
                }
            }
        }
        return params.toString();
    },

    formData(data) {
        const formData = new FormData();
        if (data) {
            for (let key in data) {
                if (Array.isArray(data[key])) {
                    for (let val of data[key]) {
                        formData.append(key, val);
                    }
                } else {
                    formData.append(key, data[key]);
                }
            }
        }
        return formData;
    }
}